import { MultiStyleText } from "slotworks/graphics/pixi/pixi-multistyle-text";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import { HitArea } from "slotworks/utils/geom/hit-area";
import Point from "slotworks/utils/geom/point";
import Logger from "slotworks/utils/logger";

export default class ButtonElement extends PIXI.Container {

    public static DISABLED_TINT: number = undefined;
    public static OVER_TINT: number = undefined;
    public static DOWN_SCALE: number = 1;

    public downScale: number;
    public overTint: number;
    public disabledTint: number;

    private states: Map<ButtonState, PIXI.Sprite>;
    private labels: Map<ButtonState, PIXI.extras.BitmapText | PIXI.Text>;
    private currentDisplay: PIXI.Sprite;
    private currentState: ButtonState = ButtonState.Up;
    private enabled: boolean;
    private selected: boolean;

    private defaultScales: Map<PIXI.Sprite, Point>;

    constructor() {
        super();
        this.states = new Map<ButtonState, PIXI.Sprite>();
        this.labels = new Map<ButtonState, PIXI.extras.BitmapText | PIXI.Text>();
        this.defaultScales = new Map<PIXI.Sprite, Point>();
        this.enabled = true;
        this.selected = false;

        this.interactive = true;
        this.buttonMode = true;

        this.downScale = ButtonElement.DOWN_SCALE;
        this.overTint = ButtonElement.OVER_TINT;
        this.disabledTint = ButtonElement.DISABLED_TINT;

        this.on(ButtonEvent.POINTER_UP.getPIXIEventString(), () => {
            this.changeState(ButtonState.Up);
        });
        this.on(ButtonEvent.POINTER_UP_OUTSIDE.getPIXIEventString(), () => {
            this.changeState(ButtonState.Up);
        });
        this.on(ButtonEvent.POINTER_OUT.getPIXIEventString(), () => {
            this.changeState(ButtonState.Up);
        });
        this.on(ButtonEvent.POINTER_OVER.getPIXIEventString(), () => {
            this.changeState(ButtonState.Over);
        });
        this.on(ButtonEvent.POINTER_DOWN.getPIXIEventString(), () => {
            this.changeState(ButtonState.Down);
        });
        this.on(ButtonEvent.POINTER_OVER.getPIXIEventString(), this.playOverSound);
        this.on(ButtonEvent.POINTER_DOWN.getPIXIEventString(), this.playPressSound);
    }

    public on(event: ButtonEvent | string, callback: Function) {
        if (event instanceof ButtonEvent) {
            return super.on(event.getPIXIEventString(), callback);
        } else {
            return super.on(event, callback);
        }
    }

    public off(event: ButtonEvent | string, callback: Function) {
        if (event instanceof ButtonEvent) {
            return super.off(event.getPIXIEventString(), callback);
        } else {
            return super.off(event, callback);
        }
    }

    public addState(state: ButtonState | string, target: PIXI.Sprite) {

        let buttonState: ButtonState;

        if (typeof state === "string") {
            buttonState = ButtonState.FromString(state);
        } else {
            buttonState = state;
        }

        if (!this.hitArea) {
            this.setHitArea(target);
        }

        this.states.set(buttonState, target);

        target.visible = false;

        target.anchor.set(0.5, 0.5);
        target.x += target.width * 0.5;
        target.y += target.height * 0.5;

        this.defaultScales.set(target, new Point(target.scale.x, target.scale.y));

        this.addChild(target);

        this.update();
    }

    public addLabelForState(label: PIXI.extras.BitmapText | PIXI.Text, state: ButtonState) {
        this.addChild(label);
        label.x -= this.x;
        label.y -= this.y;
        label.visible = false;

        this.labels.set(state, label);
        this.updateLabels();
    }

    public setHitArea(hitArea: HitArea): void {
        if (hitArea instanceof PIXI.Container) {
            this.hitArea = hitArea.getBounds();
        } else {
            this.hitArea = hitArea;
        }
    }

    public setEnabled(enabled: boolean) {
        if (this.selected) {
            enabled = false;
        }
        this.interactive = enabled;
        this.enabled = enabled;

        if (this.enabled) {
            this.currentState = ButtonState.Up;
        }

        this.update();
    }

    public setSelected(selected: boolean) {
        this.selected = selected;

        if (selected) {
            this.currentState = ButtonState.Selected;
        } else {
            this.currentState = ButtonState.Up;
        }

        this.update();

        this.setEnabled(!selected);
    }

    public setVisible(enabled: boolean) {
        this.visible = enabled;

        this.setLabelsVisibility(enabled);
    }

    public setAlpha(alpha: number) {
        this.alpha = alpha;
    }

    public setPosition(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    /**
     * Resizes to the width and height relative to the up state, if it exists, otherwise sets the width and height unchanged
     */
    public resize(width: number, height: number) {
        const upState = this.states.get(ButtonState.Up);
        if (upState) {
            this.scale.x = (width / upState.width);
            this.scale.y = (height / upState.height);
        } else {
            this.width = width;
            this.height = height;
        }
    }

    public destroy() {
        this.currentDisplay = null;
        this.states = null;

        this.removeAllListeners();

        while (this.children.length) {
            this.removeChildAt(0);
        }

        if (this.parent) {
            this.parent.removeChild(this);
        }
    }

    // TODO: Add label support
    public clone(newID?: string) {
        const toReturn = new ButtonElement();
        toReturn.name = this.name;
        toReturn.x = this.x;
        toReturn.y = this.y;

        this.states.forEach((value, key) => {
            toReturn.addState(key, new PIXI.Sprite(value.texture));
        });

        toReturn.hitArea = this.hitArea;

        if (newID) {
            toReturn.name = newID;
        } else {
            toReturn.name = this.name;
        }

        toReturn.enabled = this.enabled;
        toReturn.selected = this.selected;

        return toReturn;
    }

    private update() {
        if (!this.enabled) {
            if (!this.selected) {
                this.currentState = ButtonState.Disabled;
            }

            this.buttonMode = false;
        } else {
            this.buttonMode = true;
        }

        if (this.states && this.states.has(ButtonState.All)) {
            this.currentDisplay = this.states.get(ButtonState.All);
            this.currentDisplay.visible = true;
        } else {
            this.updateMultistateButton();
        }

        this.updateLabels();
    }

    private updateMultistateButton() {
        this.resetCurrentDisplay();

        let nextDisplay: PIXI.Sprite;

        if (this.states) {
            if (this.states.has(this.currentState)) {
                nextDisplay = this.states.get(this.currentState);
            } else if (this.currentState === ButtonState.Disabled) {
                nextDisplay = this.states.get(ButtonState.Up);
                if (this.disabledTint) {
                    nextDisplay.tint = this.disabledTint;
                }
            } else if (this.currentState === ButtonState.Over) {
                nextDisplay = this.states.get(ButtonState.Up);
                if (this.overTint) {
                    nextDisplay.tint = this.overTint;
                }
            } else if (this.currentState === ButtonState.Down) {
                nextDisplay = this.states.get(ButtonState.Up);
                if (this.downScale) {
                    nextDisplay.scale.x = this.defaultScales.get(nextDisplay).x * this.downScale;
                    nextDisplay.scale.y = this.defaultScales.get(nextDisplay).y * this.downScale;
                }
            } else if (this.states.has(ButtonState.Up)) {
                nextDisplay = this.states.get(ButtonState.Up);
            }
        }

        if (nextDisplay) {
            nextDisplay.visible = true;
        }
        if (this.currentDisplay && this.currentDisplay !== nextDisplay) {
            this.currentDisplay.visible = false;
        }
        this.currentDisplay = nextDisplay;
    }

    private resetCurrentDisplay() {
        if (this.currentDisplay) {
            this.currentDisplay.tint = 0xFFFFFF;
            this.currentDisplay.scale.x = this.defaultScales.get(this.currentDisplay).x;
            this.currentDisplay.scale.y = this.defaultScales.get(this.currentDisplay).y;
        }
    }

    private updateLabels() {
        if (this.labels.has(this.currentState)) {
            this.labels.forEach((label: MultiStyleText, state: ButtonState) => {
                if (state === this.currentState) {
                    label.visible = true;
                } else {
                    label.visible = false;
                }
            });
        }
    }

    private setLabelsVisibility(visible: boolean) {
        if (!visible) {
            this.labels.forEach((label: MultiStyleText, state: ButtonState) => {
                label.visible = false;
            });
        } else {
            this.updateLabels();
        }
    }

    private changeState(state: ButtonState) {
        this.currentState = state;

        this.update();
    }

    private playPressSound = () => {
        Services.sound.event(SoundEvent.button_press_NAME, this.name);
    }

    private playOverSound = () => {
        Services.sound.event(SoundEvent.button_over_NAME, this.name);
    }
}

export class ButtonState {
    public static Up: ButtonState = new ButtonState("up");
    public static Over: ButtonState = new ButtonState("over");
    public static Down: ButtonState = new ButtonState("down");
    public static Disabled: ButtonState = new ButtonState("disabled");
    public static Selected: ButtonState = new ButtonState("selected");
    public static All: ButtonState = new ButtonState("all");

    public static FromString(str: string = "up"): ButtonState {
        return ButtonState.buttonStates.get(str);
    }

    private static buttonStates: Map<string, ButtonState>;

    public label: string;

    constructor(label: string) {
        this.label = label;

        if (!ButtonState.buttonStates) {
            ButtonState.buttonStates = new Map<string, ButtonState>();
        }

        ButtonState.buttonStates.set(label, this);
    }
}

export class ButtonEvent {

    public static CLICK: ButtonEvent = new ButtonEvent("pointertap");
    public static POINTER_UP: ButtonEvent = new ButtonEvent("pointerup");
    public static POINTER_UP_OUTSIDE: ButtonEvent = new ButtonEvent("pointerupoutside");
    public static POINTER_OUT: ButtonEvent = new ButtonEvent("pointerout");
    public static POINTER_OVER: ButtonEvent = new ButtonEvent("pointerover");
    public static POINTER_DOWN: ButtonEvent = new ButtonEvent("pointerdown");
    public static POINTER_MOVE: ButtonEvent = new ButtonEvent("pointermove");

    private id: string;

    constructor(id: string) {
        this.id = id;
    }

    public getEventString(): string {
        Logger.warn("Deprecated method for ButtonEvent: getEventString. Use getPIXIEventString or getDOMEventString instead.");
        return this.getPIXIEventString();
    }

    public getPIXIEventString(): string {
        if (!(window as any).PointerEvent) {

            if ("ontouchstart" in window) {
                if (this.id === "pointerdown") {
                    return "touchstart";
                }
                if (this.id === "pointerup") {
                    return "touchend";
                }
                if (this.id === "pointerupoutside") {
                    return "touchendoutside";
                }
                if (this.id === "pointermove") {
                    return "touchmove";
                }
                if (this.id === "pointertap") {
                    return "tap";
                }
            } else {
                if (this.id === "pointerdown") {
                    return "mousedown";
                }
                if (this.id === "pointerup") {
                    return "mouseup";
                }
                if (this.id === "pointerupoutside") {
                    return "mouseupoutside";
                }
                if (this.id === "pointerout") {
                    return "mouseout";
                }
                if (this.id === "pointerover") {
                    return "mouseover";
                }
                if (this.id === "pointermove") {
                    return "mousemove";
                }
                if (this.id === "pointertap") {
                    return "click";
                }
            }
        }

        return this.id;
    }

    public getDOMEventString(): string {
        if (!(window as any).PointerEvent) {

            if ("ontouchstart" in window) {
                if (this.id === "pointerdown") {
                    return "touchstart";
                }
                if (this.id === "pointerup") {
                    return "touchend";
                }
                if (this.id === "pointerupoutside") {
                    return "touchcancel";
                }
                if (this.id === "pointermove") {
                    return "touchmove";
                }
            } else {
                if (this.id === "pointerdown") {
                    return "mousedown";
                }
                if (this.id === "pointerup") {
                    return "mouseup";
                }
                if (this.id === "pointermove") {
                    return "mousemove";
                }
            }
        }

        if (this.id === "pointertap") {
            return "touchstart";
        }

        return this.id;
    }
}
